import { DashboardData } from "../../models/interfaces/reports/dashboard-data";
import { CachedDashboardData } from "../../models/interfaces/reports/cached-dashboard-data";
import { User } from "../../models/interfaces/user";
import { UserTag } from "../../models/interfaces/user-tag";
import { UserStatus } from "../../models/enumerations/user-status";
import { SymmioAccessType } from "../../models/enumerations/symmio-access-type";
import { httpsCallable } from "firebase/functions";
import { collection, query, where, getDocs, limit } from "firebase/firestore";
import getFirebase from "../firebase";
import { UniqueUserInviteLink } from "../../models/interfaces/unique-user-invite-link";
import { LeadLink } from "../../models/interfaces/lead-link";
import { AssessmentUtils } from "../assessments/assessment-utils";
import ReportService from "./report-service";
import { createLogger } from "../logger";
import { ReportType } from "../../models/enumerations/report-type";
import { Report } from "../../models/interfaces/report";

const log = createLogger("FunctionService");

const { functions, db } = getFirebase();

// -----------------------------------------------------------------------------------------
// #region Service Methods
// -----------------------------------------------------------------------------------------'

const generateReportV2 = async (
    report: Report,
): Promise<void> => {

    const callable = httpsCallable(
        functions,
        "generateReportV2"
    );

    await callable({
        report,
    }).catch((e) => {
        log.error("Failed to invoke report generation function", {
            exception: e,
            reportId: report.id,
            reportType: report.type,
            organizationId: report.organizationId,
        });
    });

};

/**
 * @param {Group[]} groups
 * @param {string} organizationId
 */
const getDashboardData = async (
    groupIds: string[] | null,
    organizationId: string,
    includeInactiveUsers: boolean,

): Promise<DashboardData | null> => {
    if (organizationId == null) {
        return null;
    }

    if (!groupIds || groupIds.length === 0) {
        try {
            const q = query(
                collection(db, "organizationDashboardData"),
                where("organizationId", "==", organizationId),
                limit(1)
            );
            const snapshot = await getDocs(q);
            if (!snapshot.empty) {
                const cachedData = snapshot.docs[0].data() as CachedDashboardData;
                
                // Self-heal: check if allHistory has current month
                const now = new Date();
                const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                const hasCurrentMonth = cachedData.allHistory?.some((h: any) => 
                    h.date && h.date.startsWith(currentMonth)
                );
                
                if (hasCurrentMonth) {
                    // Pick snapshot based on filter
                    const snapshot = includeInactiveUsers ? cachedData.all : cachedData.activeOnly;
                    
                    // Build history from running totals
                    const historySource = includeInactiveUsers 
                        ? cachedData.allHistory 
                        : cachedData.activeOnlyHistory;
                    
                    const history = (historySource || []).map((entry: any) => ({
                        date: entry.date,
                        percent: entry.scoreCount === 0 
                            ? -1 
                            : Math.round(entry.totalScore / entry.scoreCount)
                    }));
                    
                    return {
                        activeUsers: cachedData.activeUsers,
                        focusAreas: snapshot?.focusAreas || [],
                        history,
                        inactiveUsers: includeInactiveUsers ? cachedData.inactiveUsers : 0,
                        pendingUsers: cachedData.pendingUsers,
                        percent: snapshot?.percent ?? -1,
                        riskFactorCodeCounts: snapshot?.riskFactorCodeCounts || [],
                        scoreBreakdown: snapshot?.scoreBreakdown || [],
                    };
                }
            }
        } catch (error) {
            log.error("Error reading dashboard cache", {
                exception: error,
                organizationId,
            });
        }
    }

    const getDashboardData = httpsCallable(functions, "getDashboardData");
    const response = await getDashboardData({
        groupIds: groupIds,
        includeInactiveUsers: includeInactiveUsers,
        monthsAgo: 11,
        organizationId: organizationId
    });
    return response.data as DashboardData;
};

const getReportData = async (
    reportId: string,
    userId: string,
) => {
    if (reportId == null || userId == null) {
        return;
    }
    const report = await ReportService.get(reportId);
    if (report && report.type === ReportType.Wellness && !report.mskScoreId) {
        const assessmentResponses = await AssessmentUtils.fetchAssessmentResponsesById(userId)
        const getReportData = httpsCallable(functions, "getReportData");
        const response = await getReportData({ reportId: reportId, userId: userId, assessmentResponses: assessmentResponses });
        return response.data;
    }
    const getReportData = httpsCallable(functions, "getReportData");
    const response = await getReportData({ reportId: reportId, userId: userId });
    return response.data;
}

/**
 * @param {User} user
 */
const updateUserRoles = async (user: User) => {
    if (user?.id == null) {
        return;
    }
    const updateUserRoles = httpsCallable(functions, "updateUserRoles");
    return updateUserRoles({ uid: user.id });
};

const getTestStripeProducts = async () => {
    const getTestStripeProducts = httpsCallable(functions, "getTestStripeProducts");
    return getTestStripeProducts();
}

const updateOrganizationPlansToStarter = async () => {
    const updateOrganizationPlansToStarter = httpsCallable(functions, "updateOrganizationPlansToStarter");
    return updateOrganizationPlansToStarter();
}

const sendInviteEmail = async (user: User, uniqueUserInviteLink: UniqueUserInviteLink) => {
    const sendInviteEmail = httpsCallable(functions, "sendInviteEmail");
    let data = { user: user, uniqueUserInviteLink: uniqueUserInviteLink };
    return sendInviteEmail(data);
}

const sendCancellationEmail = async (user: User, planInfo: any) => {
    const sendCancellationEmail = httpsCallable(functions, "sendCancellationEmail");
    let data = { user: user, planInfo: planInfo };
    return sendCancellationEmail(data);
}

const sendAddOnLicenseConfirmationEmail = async (user: User, paymentInfo: any) => {
    const sendAddOnLicenseConfirmationEmail = httpsCallable(functions, "sendAddOnLicenseConfirmationEmail");
    let data = { user: user, paymentInfo: paymentInfo };
    return sendAddOnLicenseConfirmationEmail(data);
}

const sendAddOnUserConfirmationEmail = async (user: User, paymentInfo: any) => {
    const sendAddOnUserConfirmationEmail = httpsCallable(functions, "sendAddOnUserConfirmationEmail");
    let data = { user: user, paymentInfo: paymentInfo };
    return sendAddOnUserConfirmationEmail(data);
}

const sendAddOnLicenseRemovalEmail = async (user: User, addOnInfo: any) => {
    const sendAddOnLicenseRemovalEmail = httpsCallable(functions, "sendAddOnLicenseRemovalEmail");
    let data = { user: user, addOnInfo: addOnInfo };
    return sendAddOnLicenseRemovalEmail(data);
}

const sendAddOnUserRemovalEmail = async (user: User, addOnInfo: any) => {
    const sendAddOnUserRemovalEmail = httpsCallable(functions, "sendAddOnUserRemovalEmail");
    let data = { user: user, addOnInfo: addOnInfo };
    return sendAddOnUserRemovalEmail(data);
}

const sendMySymmioAccessAssigned = async (user: User, info: any) => {
    const sendMySymmioAccessAssigned = httpsCallable(functions, "sendMySymmioAccessAssigned");
    let data = { user: user, info: info };
    return sendMySymmioAccessAssigned(data);
}

const sendMySymmioAccessRemoved = async (user: User, info: any) => {
    const sendMySymmioAccessRemoved = httpsCallable(functions, "sendMySymmioAccessRemoved");
    let data = { user: user, info: info };
    return sendMySymmioAccessRemoved(data);
}

const sendPlanPurchaseConfirmationEmail = async (user: User, paymentInfo: any, couponInfo: any | null) => {
    const sendPlanPurchaseConfirmationEmail = httpsCallable(functions, "sendPlanPurchaseConfirmationEmail");
    let data = { user: user, paymentInfo: paymentInfo, couponInfo: couponInfo };
    return sendPlanPurchaseConfirmationEmail(data);
}

const sendBusinessYearlyConfirmationEmail = async (user: User, paymentInfo: any, couponInfo: any | null) => {
    const sendBusinessYearlyConfirmationEmail = httpsCallable(functions, "sendBusinessYearlyConfirmationEmail");
    let data = { user: user, paymentInfo: paymentInfo, couponInfo: couponInfo };
    return sendBusinessYearlyConfirmationEmail(data);
}

const sendSubscriptionReactivationEmail = async (email: string, subscriptionDetails: {}) => {
    const sendSubscriptionReactivationEmail = httpsCallable(functions, "sendSubscriptionReactivationEmail");
    let data = { email: email, subscriptionDetails: subscriptionDetails };
    return sendSubscriptionReactivationEmail(data);
}

const sendAccountOwnerWelcomeEmail = async (user: User) => {
    const sendAccountOwnerWelcomeEmail = httpsCallable(functions, "sendAccountOwnerWelcomeEmail");
    let data = { user: user };
    return sendAccountOwnerWelcomeEmail(data);
}

const sendTrialWelcomeEmail = async (user: User) => {
    const sendTrialWelcomeEmail = httpsCallable(functions, "sendTrialWelcomeEmail");
    let data = { user: user };
    return sendTrialWelcomeEmail(data);
}

const sendUserSubscriptionConfirmationEmail = async (user: User) => {
    const sendUserSubscriptionConfirmationEmail = httpsCallable(functions, "sendUserSubscriptionConfirmationEmail");
    let data = { user: user };
    return sendUserSubscriptionConfirmationEmail(data);
}

const sendUserSubscriptionCancellationEmail = async (user: User) => {
    const sendUserSubscriptionCancellationEmail = httpsCallable(functions, "sendUserSubscriptionCancellationEmail");
    let data = { user: user };
    return sendUserSubscriptionCancellationEmail(data);
}

const sendRetestRequestedEmail = async (user: User, organizationName: string) => {
    const sendRetestRequestedEmail = httpsCallable(functions, "sendRetestRequestedEmail");
    let data = { user: user, organizationName: organizationName };
    return sendRetestRequestedEmail(data);
}

const generateDeeplinkUrl = async (id: string) => {
    const callableFunction = httpsCallable(functions, "generateDeeplinkUrl");
    return callableFunction({ id: id });
}

const createUserFromDeeplink = async (id: string, email: string) => {
    const callableFunction = httpsCallable(functions, "createUserFromDeeplink");

    await callableFunction({
        id: id,
        email: email
    });

    return true;
}

const updateLeadLink = async (leadLink: LeadLink) => {
    const updateLeadLink = httpsCallable(functions, "updateLeadLink");
    let data = { leadLink: leadLink };
    return updateLeadLink(data);
}

/**
 * Merges a sub-organization's users, groups, tags, links, notifications,
 * invite links and AI responses into its parent organization, then deletes
 * the sub-organization. Runs as a resumable callable; if it fails partway,
 * re-invoking resumes where it left off.
 */
const mergeSubOrganization = async (
    subOrganizationId: string,
    parentOrganizationId: string,
): Promise<{ status: string }> => {
    const callable = httpsCallable(functions, "mergeSubOrganization");
    const response = await callable({ subOrganizationId, parentOrganizationId });
    return response.data as { status: string };
}

export interface SwapUserOrganizationPatch {
    firstName?: string;
    lastName?: string;
    biologicalSex?: string;
    birthdate?: string | null;
    roles?: string[];
    status?: UserStatus;
    symmioAccess?: SymmioAccessType;
    groupId?: string | null;
    tags?: UserTag[];
    managedGroupIds?: string[];
    accessibleSubOrganizationIds?: string[];
}

/**
 * Moves a user from their current organization to another organization.
 * Super admin only; the callable rejects the move when the target
 * organization has no remaining user or license capacity.
 */
const swapUserOrganization = async (
    userId: string,
    targetOrganizationId: string,
    patch: SwapUserOrganizationPatch,
): Promise<{ status: string }> => {
    const callable = httpsCallable(functions, "swapUserOrganization");
    const response = await callable({ userId, targetOrganizationId, patch });
    return response.data as { status: string };
}

// #endregion Service Methods

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

const FunctionService = {
    generateReportV2,
    getDashboardData,
    getReportData,
    getTestStripeProducts,
    updateUserRoles,
    sendInviteEmail,
    sendCancellationEmail,
    sendAddOnLicenseConfirmationEmail,
    sendAddOnUserConfirmationEmail,
    sendAddOnLicenseRemovalEmail,
    sendAddOnUserRemovalEmail,
    sendMySymmioAccessAssigned,
    sendMySymmioAccessRemoved,
    sendPlanPurchaseConfirmationEmail,
    sendBusinessYearlyConfirmationEmail,
    sendSubscriptionReactivationEmail,
    sendAccountOwnerWelcomeEmail,
    sendTrialWelcomeEmail,
    sendUserSubscriptionConfirmationEmail,
    sendUserSubscriptionCancellationEmail,
    sendRetestRequestedEmail,
    updateOrganizationPlansToStarter,
    generateDeeplinkUrl,
    createUserFromDeeplink,
    updateLeadLink,
    mergeSubOrganization,
    swapUserOrganization,
};

export default FunctionService;
